import { Sparkles, ArrowRight, Calendar, Users, Trophy, Zap, AlertTriangle, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import useScrollReveal from "@/hooks/useScrollReveal";
import { apiGet } from "@/lib/api";

// Comeback-Aktion für ehemalige Repräsentant*innen – Termine dynamisch aus dem Backend
const HIDE_AFTER_DAYS = 7;

// Parse ISO-Datum als lokale Mitternacht (keine UTC-Verschiebung)
const parseLocalDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const staffel = [
  { ae: 1, zuzahlung: "999 €", extra: null },
  { ae: 2, zuzahlung: "0 €", extra: "+ 50 € Provision", highlight: true, label: "Empfohlen" },
  { ae: 3, zuzahlung: "0 €", extra: "+ 50 € Provision" },
  { ae: 4, zuzahlung: "0 €", extra: "+ 50 € Provision" },
  { ae: 5, zuzahlung: "0 €", extra: "+ 50 € Provision + Peeler" },
  { ae: 6, zuzahlung: "0 €", extra: "+ 50 € Provision + Gemüse-Styler inkl. Spiralschneider" }
];

const ComebackSection = () => {
  const today = startOfToday();
  const [eventDates, setEventDates] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const sectionRef = useScrollReveal(eventDates.length);

  useEffect(() => {
    apiGet("/content/comeback")
      .then((items) => setEventDates(items.map((i) => i.date).sort()))
      .catch(() => setEventDates([]))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;
  if (eventDates.length === 0) return null;

  // Nächster noch anstehender Termin
  const upcomingDates = eventDates.filter((d) => parseLocalDate(d) >= today);
  const lastDate = parseLocalDate(eventDates[eventDates.length - 1]);

  const daysSinceLastEvent = Math.round((today - lastDate) / (1000 * 60 * 60 * 24));
  if (daysSinceLastEvent > HIDE_AFTER_DAYS) return null;

  const nextDate = upcomingDates.length > 0 ? parseLocalDate(upcomingDates[0]) : null;
  const daysLeft = nextDate ? Math.round((nextDate - today) / (1000 * 60 * 60 * 24)) : null;
  const isPast = upcomingDates.length === 0;

  const formatDate = (iso) => {
    const d = parseLocalDate(iso);
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
  };

  const whatsAppLink = `https://wa.me/491721697433?text=${encodeURIComponent("Hallo Jennifer! Ich war früher Thermomix® Repräsentant*in und interessiere mich für die Comeback-Aktion. Kannst du mir mehr dazu erzählen?")}`;

  return (
    <section
      id="comeback"
      data-testid="comeback-section"
      className="section-padding relative overflow-hidden"
      ref={sectionRef}
      style={{ background: "linear-gradient(135deg, #ecfdf5 0%, #ffffff 45%, #f0fdf4 100%)" }}
    >
      <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-300/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl pointer-events-none" />

      <div className="container-custom relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 reveal">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-4">
              <Users className="w-3.5 h-3.5" /> Für ehemalige Repräsentant*innen
            </span>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[11px] font-bold uppercase tracking-wider mb-4">
              <Sparkles className="w-3 h-3" /> {eventDates.length} {eventDates.length === 1 ? "Termin" : "Termine"} verfügbar
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mb-4 font-[Outfit] leading-[1.1]">
              Comeback-Aktion 2026
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed max-w-2xl mx-auto mb-6">
              Du warst dabei und hast noch keinen TM7? Dann nutze jetzt deine Chance – schon mit{" "}
              <strong className="text-emerald-700">2 Aufträgen</strong> bekommst du deinen TM7 für{" "}
              <strong className="text-emerald-700">0 €</strong>.
            </p>

            {/* Event Dates */}
            <div className="inline-flex flex-col items-center gap-3">
              <div className="flex items-center gap-2 flex-wrap justify-center">
                {eventDates.map((d) => {
                  const dateObj = parseLocalDate(d);
                  const past = dateObj < today;
                  return (
                    <div
                      key={d}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 font-bold text-sm ${
                        past
                          ? "bg-stone-100 border-stone-200 text-stone-400 line-through"
                          : "bg-white border-emerald-500 text-emerald-700 shadow-md shadow-emerald-500/20"
                      }`}
                    >
                      <Calendar className="w-4 h-4" />
                      {formatDate(d)}
                    </div>
                  );
                })}
              </div>
              {daysLeft !== null && daysLeft > 0 && daysLeft <= 14 && (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500 text-white text-sm font-bold shadow-lg shadow-amber-500/30 animate-pulse">
                  <Clock className="w-4 h-4" />
                  Nur noch {daysLeft} {daysLeft === 1 ? "Tag" : "Tage"} bis zum nächsten Termin
                </div>
              )}
              {isPast && (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-stone-200 text-stone-600 text-sm font-bold">
                  <Clock className="w-4 h-4" /> Aktion ist ausgelaufen
                </div>
              )}
            </div>
          </div>

          {/* AE-Staffel Tabelle */}
          <div className="glass-card rounded-3xl p-6 md:p-10 mb-8 reveal reveal-delay-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0">
                <Trophy className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 font-[Outfit]">Deine Staffel auf einen Blick</h3>
                <p className="text-xs text-slate-500">Je mehr Aufträge, desto größer der Bonus</p>
              </div>
            </div>

            {/* Desktop: Grid / Mobile: Stack */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {staffel.map((s) => (
                <div
                  key={s.ae}
                  data-testid={`comeback-staffel-${s.ae}`}
                  className={`relative rounded-2xl p-4 border-2 transition-all hover:-translate-y-1 ${
                    s.highlight
                      ? "bg-gradient-to-br from-emerald-500 to-emerald-600 border-emerald-400 shadow-xl shadow-emerald-500/30 text-white"
                      : "bg-white border-stone-200 text-slate-900"
                  }`}
                >
                  {s.highlight && (
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-white rounded-full shadow-md whitespace-nowrap">
                      <span className="text-[9px] font-bold uppercase tracking-widest text-emerald-700 flex items-center gap-1">
                        <Sparkles className="w-2.5 h-2.5" /> {s.label}
                      </span>
                    </div>
                  )}
                  <div className={`text-[10px] font-bold uppercase tracking-[0.2em] mb-1 ${s.highlight ? "text-emerald-100" : "text-emerald-600"}`}>
                    {s.ae} {s.ae === 1 ? "Auftrag" : "Aufträge"}
                  </div>
                  <div className={`text-2xl font-extrabold mb-1 font-[Outfit] leading-none ${s.highlight ? "text-white" : "text-slate-900"}`}>
                    {s.zuzahlung}
                  </div>
                  {s.extra && (
                    <div className={`text-[11px] leading-snug mt-2 font-medium ${s.highlight ? "text-emerald-50" : "text-slate-500"}`}>
                      {s.extra}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Wichtig-Hinweis */}
          <div className="flex items-start gap-3 p-5 rounded-2xl bg-amber-50 border border-amber-200 mb-8 reveal reveal-delay-1">
            <div className="shrink-0 w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-amber-700 mb-1">Wichtig</div>
              <p className="text-sm text-amber-900 leading-relaxed">
                Die Aktion ist <strong>nicht mit anderen Einsteiger-Aktionen kombinierbar</strong>. Sie gilt ausschließlich für den Wiedereinstieg ehemaliger Repräsentant*innen.
              </p>
            </div>
          </div>

          {/* CTA */}
          {!isPast && (
            <div className="text-center reveal reveal-delay-2">
              <a
                href={whatsAppLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="comeback-cta"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold text-base shadow-xl shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:-translate-y-0.5 transition-all group"
              >
                <Zap className="w-5 h-5" />
                Comeback starten – jetzt Info per WhatsApp
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <p className="text-xs text-slate-500 mt-4">
                Oder schreib mir direkt per E-Mail oder ruf mich an: <a href="tel:+491721697433" className="font-semibold text-emerald-700 hover:underline">0172 1697433</a>
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ComebackSection;
